import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Phone, ArrowRight, AlertTriangle, Settings, Mail, Lock, Eye, EyeOff, KeyRound, ShieldCheck } from 'lucide-react'
import { RecaptchaVerifier } from 'firebase/auth'
import { useAuthStore } from '@/store/authStore'
import { authService } from '@/services/firebase/auth'
import { getFirebaseStatus, auth, isFirebaseAvailable } from '@/services/firebase/config'
import type { Auth } from 'firebase/auth'
import type { User } from '@/types'
import { ROUTES, VALIDATION } from '@/constants'
import toast from 'react-hot-toast'

const FirebaseConfigBanner = () => {
  const status = getFirebaseStatus()
  if (status.isAvailable) return null

  return (
    <div className="mb-6 rounded-xl border border-orange-200 bg-orange-50 p-4">
      <div className="flex items-start gap-3">
        <AlertTriangle size={20} className="text-orange-500 mt-0.5 shrink-0" />
        <div>
          <p className="font-semibold text-orange-800 text-sm">Firebase Not Configured</p>
          <p className="text-orange-700 text-xs mt-1">
            {status.isConfigured
              ? 'Firebase initialization failed. Check your credentials.'
              : 'Add your Firebase project credentials to the .env file to enable authentication.'}
          </p>
          <div className="mt-2 flex items-center gap-1.5">
            <Settings size={12} className="text-orange-600" />
            <code className="text-xs text-orange-700 bg-orange-100 px-1.5 py-0.5 rounded">
              VITE_FIREBASE_API_KEY=your_key
            </code>
          </div>
        </div>
      </div>
    </div>
  )
}

const LoginPage = () => {
  const navigate = useNavigate()
  const { login } = useAuthStore()

  // Login mode toggle
  const [loginMode, setLoginMode] = useState<'phone' | 'email'>('phone')

  // Phone OTP state
  const [step, setStep] = useState<'phone' | 'otp'>('phone')
  const [phone, setPhone] = useState('')
  const [otp, setOtp] = useState('')
  const [countdown, setCountdown] = useState(30)
  const recaptchaRef = useRef<RecaptchaVerifier | null>(null)

  // Email/password state
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)

  // Force change password state (first-login / admin-reset)
  const [mustChangePwd, setMustChangePwd] = useState(false)
  const [pendingUser, setPendingUser] = useState<User | null>(null)
  const [newPwd, setNewPwd] = useState('')
  const [newPwdConfirm, setNewPwdConfirm] = useState('')
  const [showNewPwd, setShowNewPwd] = useState(false)
  const [changingPwd, setChangingPwd] = useState(false)

  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (step === 'otp' && countdown > 0) {
      const timer = setTimeout(() => setCountdown(c => c - 1), 1000)
      return () => clearTimeout(timer)
    }
  }, [step, countdown])

  // Initialize reCAPTCHA once on mount
  useEffect(() => {
    if (!isFirebaseAvailable || !auth) return
    const firebaseAuth = auth as Auth
    // In dev, disable app verification so test phone numbers work without reCAPTCHA
    if (import.meta.env.DEV) {
      firebaseAuth.settings.appVerificationDisabledForTesting = true
    }
    recaptchaRef.current = new RecaptchaVerifier(firebaseAuth, 'recaptcha-container', {
      size: 'invisible',
      callback: () => {},
      'expired-callback': () => {
        recaptchaRef.current = null
      }
    })
    return () => {
      recaptchaRef.current?.clear()
      recaptchaRef.current = null
    }
  }, [])

  const handleSendOTP = async () => {
    if (!VALIDATION.PHONE_REGEX.test(phone)) {
      toast.error('Please enter a valid 10-digit mobile number')
      return
    }

    setIsLoading(true)
    try {
      // Re-initialize reCAPTCHA if cleared (e.g. after expiry or resend)
      if (!recaptchaRef.current && auth) {
        const firebaseAuth = auth as Auth
        if (import.meta.env.DEV) {
          firebaseAuth.settings.appVerificationDisabledForTesting = true
        }
        recaptchaRef.current = new RecaptchaVerifier(firebaseAuth, 'recaptcha-container', {
          size: 'invisible',
          callback: () => {},
        })
      }

      // Send OTP
      await authService.sendOTP(phone, recaptchaRef.current)
      setStep('otp')
      setCountdown(30)
      toast.success('OTP sent successfully!')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to send OTP')
    } finally {
      setIsLoading(false)
    }
  }

  const handleVerifyOTP = async () => {
    if (otp.length !== 6) {
      toast.error('Please enter a valid 6-digit OTP')
      return
    }

    setIsLoading(true)
    try {
      const firebaseUser = await authService.verifyOTP(otp)
      console.log('[Auth] OTP verified, uid:', firebaseUser.uid)

      // Try to fetch existing profile — don't block login if Firestore fails
      let profile = null
      try {
        profile = await authService.getUserProfile(firebaseUser.uid, firebaseUser.phoneNumber ?? `+91${phone}`)
        console.log('[Auth] Profile fetched:', profile)
      } catch (firestoreErr) {
        console.warn('[Auth] Could not fetch profile (Firestore may be unavailable):', firestoreErr)
      }

      if (profile) {
        login(profile)
        toast.success('Welcome back!')
      } else {
        // New user or Firestore unavailable — build a minimal profile from Firebase user
        const newUser = {
          uid: firebaseUser.uid,
          id: firebaseUser.uid,
          phoneNumber: firebaseUser.phoneNumber ?? `+91${phone}`,
          name: firebaseUser.displayName ?? 'New User',
          role: 'engineer' as const,
          isActive: true,
          createdAt: new Date(),
          updatedAt: new Date()
        }
        // Attempt to save profile but don't block on failure
        authService.saveUserProfile(newUser).catch((err) => {
          console.warn('[Auth] Could not save profile to Firestore:', err)
        })
        login(newUser)
        toast.success('Signed in successfully!')
      }
      navigate(ROUTES.DASHBOARD)
    } catch (error) {
      console.error('[Auth] OTP verification failed:', error)
      toast.error(error instanceof Error ? error.message : 'Invalid OTP. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleResendOTP = () => {
    if (countdown === 0) {
      handleSendOTP()
    }
  }

  const handleEmailLogin = async () => {
    if (!email.trim() || !VALIDATION.EMAIL_REGEX.test(email)) {
      toast.error('Please enter a valid email address')
      return
    }
    if (!password) {
      toast.error('Please enter your password')
      return
    }
    setIsLoading(true)
    try {
      const firebaseUser = await authService.loginWithEmail(email.trim(), password)
      console.log('[Auth] Email login, uid:', firebaseUser.uid)

      let profile = null
      try {
        profile = await authService.getUserProfile(firebaseUser.uid, firebaseUser.email ?? undefined)
      } catch (e) {
        console.warn('[Auth] Could not fetch profile:', e)
      }

      const resolvedUser: User = profile ?? {
        uid: firebaseUser.uid,
        id: firebaseUser.uid,
        phoneNumber: firebaseUser.phoneNumber ?? '',
        name: firebaseUser.displayName ?? email.split('@')[0],
        email: firebaseUser.email ?? email,
        role: 'admin' as const,
        isActive: true,
        createdAt: new Date(),
        updatedAt: new Date()
      }
      if (!profile) authService.saveUserProfile(resolvedUser).catch(console.warn)

      // Check if user must change their password (first login / admin reset)
      const mustChange = profile
        ? authService.parseMustChangePassword(profile as unknown as Record<string, unknown>)
        : false

      if (mustChange || password === '123456') {
        // Hold off on logging in — show forced password change screen
        setPendingUser(resolvedUser)
        setMustChangePwd(true)
        return
      }

      login(resolvedUser)
      toast.success(profile ? 'Welcome back!' : 'Signed in successfully!')
      navigate(ROUTES.DASHBOARD)
    } catch (error) {
      console.error('[Auth] Email login failed:', error)
      const msg = error instanceof Error ? error.message : 'Login failed'
      if (msg.includes('user-not-found') || msg.includes('wrong-password') || msg.includes('invalid-credential')) {
        toast.error('Invalid email or password')
      } else {
        toast.error(msg)
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleChangePassword = async () => {
    if (newPwd.length < 6) {
      toast.error('Password must be at least 6 characters')
      return
    }
    if (newPwd !== newPwdConfirm) {
      toast.error('Passwords do not match')
      return
    }
    if (newPwd === '123456') {
      toast.error('Please choose a different password than the default')
      return
    }
    if (!pendingUser) return
    setChangingPwd(true)
    try {
      await authService.changePasswordFirstTime(pendingUser.uid, newPwd)
      login(pendingUser)
      toast.success('Password updated! Welcome.')
      navigate(ROUTES.DASHBOARD)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update password')
    } finally {
      setChangingPwd(false)
    }
  }

  const { isAvailable: firebaseAvailable } = getFirebaseStatus()

  const Spinner = () => (
    <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
    </svg>
  )

  const inputCls = "flex-1 px-3 py-3.5 text-gray-900 placeholder-gray-400 text-sm outline-none bg-transparent"
  const inputWrapCls = "flex items-center rounded-2xl border border-gray-200 bg-white/80 shadow-sm overflow-hidden focus-within:ring-2 focus-within:ring-blue-500/30 focus-within:border-blue-400 transition-all duration-200 hover:border-gray-300"
  const btnCls = "w-full flex items-center justify-center gap-2 px-6 py-4 text-white font-bold rounded-2xl transition-all duration-200 text-sm disabled:cursor-not-allowed disabled:opacity-60 active:scale-[0.98]"

  if (mustChangePwd) {
    return (
      <div className="w-full">
        <div className="flex flex-col items-center gap-2 mb-7">
          <div className="w-14 h-14 rounded-3xl flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg,#7c3aed,#6366f1)', boxShadow: '0 4px 20px rgba(124,58,237,0.3)' }}>
            <KeyRound size={24} className="text-white" />
          </div>
          <h2 className="text-lg font-black text-gray-900">Set Your Password</h2>
          <p className="text-xs text-center text-gray-500 max-w-xs">
            {password === '123456'
              ? 'You logged in with the default password. Please set a new password to continue.'
              : 'Your password was reset by an admin. Please set a new password to continue.'}
          </p>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-xs font-black text-gray-500 uppercase tracking-wider mb-2">New Password</label>
            <div className={inputWrapCls}>
              <div className="pl-4"><Lock size={15} className="text-gray-400" /></div>
              <input
                type={showNewPwd ? 'text' : 'password'}
                value={newPwd}
                onChange={e => setNewPwd(e.target.value)}
                placeholder="At least 6 characters"
                className={inputCls}
              />
              <button type="button" onClick={() => setShowNewPwd(s => !s)} className="pr-4 text-gray-400 hover:text-gray-600">
                {showNewPwd ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-xs font-black text-gray-500 uppercase tracking-wider mb-2">Confirm Password</label>
            <div className={inputWrapCls}>
              <div className="pl-4"><Lock size={15} className="text-gray-400" /></div>
              <input
                type={showNewPwd ? 'text' : 'password'}
                value={newPwdConfirm}
                onChange={e => setNewPwdConfirm(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleChangePassword()}
                placeholder="Repeat new password"
                className={inputCls}
              />
            </div>
          </div>

          <button
            onClick={handleChangePassword}
            disabled={changingPwd}
            className={btnCls}
            style={{ background: 'linear-gradient(135deg,#7c3aed,#6366f1)', boxShadow: '0 4px 20px rgba(124,58,237,0.3)' }}>
            {changingPwd
              ? <><Spinner /> Saving…</>
              : <><ShieldCheck size={16} /> Set Password &amp; Sign In</>}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full">
      <FirebaseConfigBanner />

      {/* ── Mode toggle ── */}
      {step === 'phone' && (
        <div className="flex items-center bg-gray-100/80 rounded-2xl p-1 mb-7 border border-gray-200/60">
          {(['phone', 'email'] as const).map(mode => (
            <button
              key={mode}
              onClick={() => setLoginMode(mode)}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 ${
                loginMode === mode
                  ? 'bg-white text-gray-900 shadow-sm border border-gray-200/80'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {mode === 'phone' ? <Phone size={13} /> : <Mail size={13} />}
              {mode === 'phone' ? 'Phone OTP' : 'Email'}
            </button>
          ))}
        </div>
      )}

      {/* ── Phone OTP flow ── */}
      {loginMode === 'phone' && (
        <>
          {step === 'phone' ? (
            <div className="space-y-5">
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Mobile Number</label>
                <div className={inputWrapCls}>
                  <div className="flex items-center pl-4 pr-3 gap-1.5 border-r border-gray-200">
                    <span className="text-base">🇮🇳</span>
                    <span className="text-sm font-bold text-gray-700">+91</span>
                  </div>
                  <input
                    type="tel" inputMode="numeric"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    placeholder="98765 43210"
                    className={inputCls}
                  />
                  {phone.length === 10 && (
                    <div className="flex items-center pr-4">
                      <div className="w-5 h-5 bg-emerald-500 rounded-full flex items-center justify-center shadow-sm">
                        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    </div>
                  )}
                </div>
                <p className="mt-2 text-xs text-gray-400">We'll send a one-time password to this number</p>
              </div>

              <div id="recaptcha-container" className="hidden" />

              <button
                onClick={handleSendOTP}
                disabled={isLoading || !firebaseAvailable}
                className={btnCls}
                style={{ background: firebaseAvailable ? 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)' : undefined, boxShadow: firebaseAvailable ? '0 4px 20px rgba(37,99,235,0.35)' : undefined }}
              >
                {isLoading ? <Spinner /> : <>{firebaseAvailable ? 'Send OTP' : 'Firebase Not Configured'}{firebaseAvailable && <ArrowRight size={16} />}</>}
              </button>
            </div>
          ) : (
            <div className="space-y-5">
              {/* Sent to banner */}
              <div className="flex items-center gap-3 p-3.5 rounded-2xl"
                style={{ background: 'linear-gradient(135deg, rgba(37,99,235,0.08) 0%, rgba(6,182,212,0.06) 100%)', border: '1px solid rgba(37,99,235,0.15)' }}>
                <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
                  style={{ background: 'linear-gradient(135deg, #2563eb, #06b6d4)' }}>
                  <Phone size={16} className="text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] text-blue-500 font-semibold uppercase tracking-wide">OTP sent to</p>
                  <p className="text-sm font-bold text-gray-900">+91 {phone}</p>
                </div>
                <button onClick={() => setStep('phone')} className="text-xs text-blue-600 hover:text-blue-800 font-semibold bg-blue-50 px-2.5 py-1 rounded-lg transition-colors">
                  Change
                </button>
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Enter 6-digit OTP</label>
                <input
                  type="tel" inputMode="numeric"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="• • • • • •"
                  maxLength={6}
                  className="w-full px-4 py-4 text-center text-3xl font-black tracking-[0.6em] text-gray-900 border border-gray-200 rounded-2xl bg-white/80 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition-all placeholder-gray-200"
                />
              </div>

              <div className="text-center">
                {countdown > 0 ? (
                  <p className="text-sm text-gray-400">Resend in <span className="font-bold text-gray-700 tabular-nums">{countdown}s</span></p>
                ) : (
                  <button onClick={handleResendOTP} className="text-sm text-blue-600 hover:text-blue-800 font-semibold">
                    Didn't receive it? Resend OTP →
                  </button>
                )}
              </div>

              <button
                onClick={handleVerifyOTP}
                disabled={isLoading || otp.length !== 6}
                className={btnCls}
                style={{ background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)', boxShadow: '0 4px 20px rgba(37,99,235,0.35)' }}
              >
                {isLoading ? <Spinner /> : <><span>Verify & Sign In</span><ArrowRight size={16} /></>}
              </button>
            </div>
          )}
        </>
      )}

      {/* ── Email / Password flow ── */}
      {loginMode === 'email' && (
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Email Address</label>
            <div className={inputWrapCls}>
              <div className="pl-4"><Mail size={15} className="text-gray-400" /></div>
              <input
                type="email" value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@bsnl.in"
                className={inputCls}
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Password</label>
            <div className={inputWrapCls}>
              <div className="pl-4"><Lock size={15} className="text-gray-400" /></div>
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleEmailLogin()}
                placeholder="••••••••••"
                className={inputCls}
              />
              <button type="button" onClick={() => setShowPassword(s => !s)}
                className="pr-4 text-gray-400 hover:text-gray-600 transition-colors">
                {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
          </div>

          <div className="pt-1">
            <button
              onClick={handleEmailLogin}
              disabled={isLoading || !firebaseAvailable}
              className={btnCls}
              style={{ background: firebaseAvailable ? 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)' : undefined, boxShadow: firebaseAvailable ? '0 4px 20px rgba(37,99,235,0.35)' : undefined }}
            >
              {isLoading ? <Spinner /> : <>{firebaseAvailable ? 'Sign In' : 'Firebase Not Configured'}{firebaseAvailable && <ArrowRight size={16} />}</>}
            </button>
          </div>

          <p className="text-xs text-center text-gray-400 mt-2">
            Forgot password?{' '}
            <span className="text-blue-600 font-semibold">
              Contact your admin to reset it — default password is <span className="font-black text-gray-700">123456</span>
            </span>
          </p>
        </div>
      )}

      <div className="mt-7 pt-6 border-t border-gray-100 text-center">
        <p className="text-xs text-gray-400">
          By signing in you agree to our{' '}
          <span className="text-blue-600 cursor-pointer hover:underline font-medium">Terms</span>
          {' '}·{' '}
          <span className="text-blue-600 cursor-pointer hover:underline font-medium">Privacy</span>
        </p>
      </div>
    </div>
  )
}

export default LoginPage
